import { DcmTagSheetCreativeSource } from '../../../../../apis/campaignGraphqlManager/dcmTagSheet/types';
import { TAG_VALIDATION_MESSAGE, URL_REGEX } from '../../../../../common/constants';
import { adTagTypes, isRecognizedConfig } from '../../../../../configs';
import type { TagSheetAd, TagSheetLineItem, TagSheetParseResult, TagSheetTag } from '../types';
import {
  AD_NAME_REQUIRED,
  CREATIVE_URL_REQUIRED,
  DUPLICATE_BILLABLE_TAG,
  DUPLICATE_CLICK_TAG,
  DUPLICATE_NON_BILLABLE_URL,
  TAG_POSITION_REQUIRED,
  TAG_TYPE_REQUIRED,
  TAG_URL_REQUIRED,
} from './constants';
import type { AdValidationError, LineItemsValidationResult, TagValidationError } from './types';
import { validateDates } from './validateDates';
import { getAdTypeValidationErrors } from './validateTypes';

type TagCounters = {
  billableCount: number;
  clickCount: number;
  nonBillableUrls: Set<string>;
};

// Validates tag type uniqueness (Billable/Click) and tag URL (required, format, Non-Billable duplicates).
// Mutates counters to track duplicates across sibling tags within one ad.
const validateTag = (tag: TagSheetTag, counters: TagCounters): TagValidationError => {
  const tagError: TagValidationError = {};

  if (!isRecognizedConfig(tag.type)) {
    tagError.type = TAG_TYPE_REQUIRED;
  }

  if (!isRecognizedConfig(tag.position)) {
    tagError.position = TAG_POSITION_REQUIRED;
  }

  if (tag.type.key === adTagTypes.BILLABLE.key) counters.billableCount++;
  if (tag.type.key === adTagTypes.CLICK.key) counters.clickCount++;

  if (tag.type.key === adTagTypes.BILLABLE.key && counters.billableCount > 1) {
    tagError.type = DUPLICATE_BILLABLE_TAG;
  }

  if (tag.type.key === adTagTypes.CLICK.key && counters.clickCount > 1) {
    tagError.type = DUPLICATE_CLICK_TAG;
  }

  if (!tag.tagUrl.trim()) {
    tagError.tagUrl = TAG_URL_REQUIRED;
  } else if (!URL_REGEX.test(tag.tagUrl)) {
    tagError.tagUrl = TAG_VALIDATION_MESSAGE;
  } else if (tag.type.key === adTagTypes.NON_BILLABLE.key) {
    const normalizedUrl = tag.tagUrl.trim().toLowerCase();

    if (counters.nonBillableUrls.has(normalizedUrl)) {
      tagError.tagUrl = DUPLICATE_NON_BILLABLE_URL;
    } else {
      counters.nonBillableUrls.add(normalizedUrl);
    }
  }

  return tagError;
};

// Validates ad-level fields (name, VAST URL) and all its tags; returns null if no errors found.
export const validateAd = (ad: TagSheetAd): AdValidationError | null => {
  const errors: AdValidationError = { tags: [] };
  let hasError = false;

  if (!ad.adName.trim()) {
    errors.adName = AD_NAME_REQUIRED;
    hasError = true;
  }

  if (ad.creative.source === DcmTagSheetCreativeSource.VAST) {
    if (!ad.creative.creativeUrl?.trim()) {
      errors.creativeUrl = CREATIVE_URL_REQUIRED;
      hasError = true;
    } else if (!URL_REGEX.test(ad.creative.creativeUrl)) {
      errors.creativeUrl = TAG_VALIDATION_MESSAGE;
      hasError = true;
    }
  }

  const counters: TagCounters = { billableCount: 0, clickCount: 0, nonBillableUrls: new Set() };

  errors.tags = ad.tags.map((tag) => {
    const tagError = validateTag(tag, counters);

    if (tagError.type || tagError.tagUrl || tagError.position) hasError = true;

    return tagError;
  });

  return hasError ? errors : null;
};

const hasAdValidationError = (error: AdValidationError): boolean =>
  Boolean(
    error.adName ||
      error.creativeUrl ||
      error.adType ||
      error.creativeType ||
      error.unsupportedCreativeType ||
      error.dateRange ||
      error.tags.some((tagError) => tagError.type || tagError.tagUrl || tagError.position)
  );

export const validateMappedAd = (ad: TagSheetAd, lineItem: TagSheetLineItem): AdValidationError | null => {
  const fieldError = validateAd(ad);
  const typeErrors = getAdTypeValidationErrors(ad, lineItem);
  const dateError = validateDates(ad, lineItem);

  const adHasNoErrors =
    !fieldError && !typeErrors.adType && !typeErrors.creativeType && !typeErrors.unsupportedCreativeType && !dateError;

  if (adHasNoErrors) {
    return null;
  }

  const merged: AdValidationError = {
    tags: fieldError?.tags ?? ad.tags.map(() => ({})),
    ...fieldError,
    ...typeErrors,
    ...(dateError ? { dateRange: dateError } : {}),
  };

  return hasAdValidationError(merged) ? merged : null;
};

export type SubmittableMappedAd = {
  lineItem: TagSheetLineItem;
  tagSheetAd: TagSheetAd;
};

/** Mapped ads that pass field, type, and date validation. */
export const getSubmittableMappedAds = (parseResult: TagSheetParseResult): SubmittableMappedAd[] =>
  parseResult.lineItems.flatMap((lineItem) =>
    lineItem.tagSheetAds
      .filter((ad) => validateMappedAd(ad, lineItem) === null)
      .map((tagSheetAd) => ({ lineItem, tagSheetAd }))
  );

export const validateLineItems = (parseResult: TagSheetParseResult): LineItemsValidationResult => {
  const adErrors: Record<string, AdValidationError> = {};

  parseResult.lineItems.forEach((lineItem) => {
    lineItem.tagSheetAds.forEach((ad) => {
      const error = validateMappedAd(ad, lineItem);

      if (error) {
        adErrors[ad.id] = error;
      }
    });
  });

  const allLineItemsEmpty = parseResult.lineItems.every((li) => li.tagSheetAds.length === 0);
  const canSave = !allLineItemsEmpty && Object.keys(adErrors).length === 0;

  return { canSave, adErrors };
};
